import React, { useState, useRef, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import './MultiModalSignature.css';

// Simple animation utilities to replace framer-motion
const motion = {
  div: ({ children, style, className, initial, animate, exit, variants, transition, onClick, ...props }) => (
    <div
      className={`${className || ''} motion-element`}
      style={{ ...style, transition: 'all 0.3s ease' }}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  )
};

const AnimatePresence = ({ children }) => <div className="animate-presence">{children}</div>;

// Simple dropzone implementation
const useDropzone = ({ onDrop, accept, maxSize, multiple }) => {
  const getRootProps = () => ({
    onClick: () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = multiple || false;
      input.accept = Object.keys(accept || {}).join(',');
      input.onchange = (e) => {
        const files = Array.from(e.target.files);
        if (maxSize) {
          const validFiles = files.filter(file => file.size <= maxSize);
          if (validFiles.length < files.length) {
            alert('Some files exceed the maximum size limit');
          }
          onDrop(validFiles);
        } else {
          onDrop(files);
        }
      };
      input.click();
    },
    onDrop: (e) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      onDrop(files);
    },
    onDragOver: (e) => e.preventDefault(),
    onDragEnter: (e) => e.preventDefault()
  });

  const getInputProps = () => ({
    style: { display: 'none' }
  });

  return { getRootProps, getInputProps, isDragActive: false };
};

const SIGNATURE_MODES = {
  DRAW: 'draw',
  // UPLOAD: 'upload'
};


const MultiModalSignature = ({ onSignatureChange, initialSignature, isOpen, onClose }) => {
  const [activeMode, setActiveMode] = useState(SIGNATURE_MODES.DRAW);
  const [signature, setSignature] = useState(initialSignature || null);
  const [penColor, setPenColor] = useState('#000000');
  const [penWidth, setPenWidth] = useState(2);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const canvasRef = useRef();

  // Handle file upload with automatic optimization
  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setIsProcessing(true);
    
    try {
      const optimizedImage = await optimizeImage(file);
      setUploadedImage(optimizedImage);
      setSignature(optimizedImage);
    } catch (error) {
      console.error('Error processing image:', error);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg']
    },
    maxSize: 512 * 1024, // 512KB
    multiple: false
  });

  // Image optimization function
  const optimizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Set canvas size with maximum dimensions
        const maxWidth = 400;
        const maxHeight = 200;
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        // Clear background and make it transparent
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Convert to data URL with compression
        const dataUrl = canvas.toDataURL('image/png', 0.8);
        resolve(dataUrl);
      };
      
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle canvas signature
  const handleCanvasEnd = () => {
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.getTrimmedCanvas().toDataURL('image/png');
      setSignature(dataUrl);
    }
  };


  // Clear signature
  const clearSignature = () => {
    setSignature(null);
    setUploadedImage(null);
    if (canvasRef.current) {
      canvasRef.current.clear();
    }
  };

  // Save signature
  const saveSignature = () => {
    if (signature) {
      onSignatureChange(signature);
      onClose();
    }
  };

  const modeVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="signature-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div 
            className="signature-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="signature-modal-header">
              <h3>Create Your Signature</h3>
              <button className="close-button" onClick={onClose}>×</button>
            </div>

            {/* Mode Selection */}
            <div className="signature-modes">
              {Object.values(SIGNATURE_MODES).map((mode) => (
                <button
                  key={mode}
                  className={`mode-button ${activeMode === mode ? 'active' : ''}`}
                  onClick={() => setActiveMode(mode)}
                >
                  {mode === 'draw' && '✍️ Draw'}
                  {mode === 'upload' && '📁 Upload'}
                  {mode === 'type' && '⌨️ Type'}
                </button>
              ))}
            </div>

            <div className="signature-content">
              <AnimatePresence mode="wait">
                {/* Draw Mode */}
                {activeMode === SIGNATURE_MODES.DRAW && (
                  <motion.div
                    key="draw"
                    variants={modeVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="draw-mode"
                  >
                    <div className="canvas-controls">
                      <div className="control-group">
                        <label>Pen Color:</label>
                        <input
                          type="color"
                          value="#000000"
                          onChange={(e) => setPenColor("#000000")}
                        />
                      </div>
                      <div className="control-group">
                        <label>Pen Width:</label>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={penWidth}
                          onChange={(e) => setPenWidth(parseInt(e.target.value))}
                        />
                      </div>
                    </div>
                    
                    <div className="canvas-container">
                      <SignatureCanvas
                        ref={canvasRef}
                        canvasProps={{
                          className: 'signature-canvas',
                          width: 500,
                          height: 200
                        }}
                        penColor={penColor}
                        minWidth={penWidth}
                        maxWidth={penWidth + 2}
                        onEnd={handleCanvasEnd}
                        velocityFilterWeight={0.7}
                        throttle={16}
                      />
                    </div>
                    
                    <div className="canvas-actions">
                      <button 
                        className="action-button secondary"
                        onClick={() => canvasRef.current?.clear()}
                      >
                        Clear
                      </button>
                      <button 
                        className="action-button secondary"
                        onClick={() => canvasRef.current?.fromData(canvasRef.current?.toData().slice(0, -1))}
                      >
                        Undo
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Upload Mode */}
                {activeMode === SIGNATURE_MODES.UPLOAD && (
                  <motion.div
                    key="upload"
                    variants={modeVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="upload-mode"
                  >
                    <div 
                      {...getRootProps()} 
                      className={`upload-dropzone ${isDragActive ? 'active' : ''} ${isProcessing ? 'processing' : ''}`}
                    >
                      <input {...getInputProps()} />
                      {isProcessing ? (
                        <div className="processing-indicator">
                          <div className="spinner"></div>
                          <p>Processing image...</p>
                        </div>
                      ) : uploadedImage ? (
                        <div className="uploaded-preview">
                          <img src={uploadedImage} alt="Uploaded signature" />
                          <p>Signature uploaded successfully!</p>
                        </div>
                      ) : (
                        <div className="upload-prompt">
                          <div className="upload-icon">📁</div>
                          <p>
                            {isDragActive 
                              ? 'Drop the image here...' 
                              : 'Drag & drop a signature image, or click to select'}
                          </p>
                          <small>Supports PNG, JPG, JPEG, SVG (max 512KB)</small>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Type Mode */}
                {/* {activeMode === SIGNATURE_MODES.TYPE && (
                  <motion.div
                    key="type"
                    variants={modeVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="type-mode"
                  >
                    <div className="type-controls">
                      <div className="control-group">
                        <label>Font Style:</label>
                        <select 
                          value={selectedFont.value}
                          onChange={(e) => setSelectedFont(FONTS.find(f => f.value === e.target.value))}
                        >
                          {FONTS.map(font => (
                            <option key={font.value} value={font.value}>
                              {font.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="control-group">
                        <label>Color:</label>
                        <input
                          type="color"
                          value={penColor}
                          onChange={(e) => setPenColor(e.target.value)}
                        />
                      </div>
                    </div>
                    
                    <div className="type-input-container">
                      <input
                        type="text"
                        placeholder="Type your name here..."
                        value={typedText}
                        onChange={(e) => setTypedText(e.target.value)}
                        className="signature-text-input"
                        maxLength={50}
                      />
                    </div>
                    
                    <div className="type-preview">
                      <div 
                        ref={typedSignatureRef}
                        className={`typed-signature-preview ${selectedFont.value}`}
                        style={{ color: penColor }}
                      >
                        {typedText || 'Your signature will appear here'}
                      </div>
                    </div>
                    
                    <button 
                      className="action-button primary"
                      onClick={handleTypedSignature}
                      disabled={!typedText.trim()}
                    >
                      Generate Signature
                    </button>
                  </motion.div>
                )} */}
              </AnimatePresence>

              {/* Signature Preview */}
              {signature && (
                <motion.div 
                  className="signature-preview"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <h4>Signature Preview:</h4>
                  <div className="preview-container">
                    <img src={signature} alt="Signature preview" />
                  </div>
                </motion.div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="signature-modal-actions">
              <button className="action-button secondary" onClick={clearSignature}>
                Clear All
              </button>
              <button className="action-button secondary" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="action-button primary"
                onClick={saveSignature}
                disabled={!signature}
              >
                Save Signature
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MultiModalSignature;